import React from "react";

import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";

import { ArtworkCard } from "../../../components/gallery/Cards";

import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

import ArtworkFrame from "../../../components/gallery/ArtworkFrame";

import WITHDRAWN_IMAGE from "../../../images/Banner/withdrawn.jpg";

import { tokens } from "../../../theme/galleryTheme";

const clamp = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const RESERVE_STATUSES = [
  "RESERVE_NOT_MET",
  "PASS",
  "UNSOLD",
];

/**
 * Lot card using the same visual system as
 * Home -> ActivitySection -> ArtworkCard.
 *
 * Business logic and callbacks remain owned
 * by the Lot page.
 */
export default function LotActivityCard({
  value,
  onNavigate,
  onFavorite,
  showHp,
  status,
  yourId,
  viewMode = "grid",
}) {
  const data = value.data || {};

  const image =
    data.isWithdrawn === 1
      ? WITHDRAWN_IMAGE
      : value.images?.[0]?.img_url ||
        value.image?.[0]?.img_url ||
        "https://placehold.co/400";

  const artist =
    data.artist?.replace(/(<([^>]+)>)/gi, "") || "";

  const title =
    data.title?.replace(/(<([^>]+)>)/gi, "") || "";

  /*
   * SIZE
   *
   * Uses the size value supplied by the API.
   * If there is no size, nothing is displayed.
   */
  const size =
    data.size ||
    "";

  const highestBid = value.myBid?.length
    ? Math.max(
        ...value.myBid.map(
          (bid) => Number(bid.amount) || 0
        )
      )
    : null;

  const isWinning =
    yourId === data.userBid &&
    !RESERVE_STATUSES.includes(data.lot_status);

  const hasBid =
    highestBid !== null &&
    highestBid > 0 &&
    data.hp > 0 &&
    data.isWithdrawn !== 1;

  const isActive =
    status !== "ended_auction" &&
    data.isDone !== 1 &&
    data.isWithdrawn !== 1;

  let price = data.starting_price || 0;
  let priceLabel = "";

  if (Number(showHp) === 1) {
    if (
      RESERVE_STATUSES.includes(
        data.lot_status
      ) ||
      data.lot_status === "PENDING" ||
      !data.hammer_price
    ) {
      price = data.starting_price || 0;
    } else {
      price = data.hammer_price || 0;
    }

    priceLabel =
      data.isWithdrawn === 1
        ? ""
        : data.hammer_price > 1
        ? "Sold · "
        : "";
  }

  // Display only: label the price as the starting price
  // whenever `price` above is the starting price.
  const isStartingPrice =
    Number(showHp) !== 1 ||
    RESERVE_STATUSES.includes(
      data.lot_status
    ) ||
    data.lot_status === "PENDING" ||
    !data.hammer_price;

  const priceText = `${
    isStartingPrice ? "" : priceLabel
  }₱${Number(price).toLocaleString()}`;

  const showCurrentBid =
    data.isWithdrawn !== 1 &&
    data.isDone !== 1 &&
    data.hp > 0;

  const statusTone = isWinning
    ? {
        color: "#2E7D32",
        bg: "#F1F8E9",
      }
    : {
        color: tokens.color.live,
        bg: "#FFF5F5",
      };

  const activityStatus = (() => {
    if (!hasBid) return null;

    const amount = `₱${highestBid.toLocaleString()}`;

    if (yourId === data.userBid) {
      if (
        RESERVE_STATUSES.includes(
          data.lot_status
        )
      ) {
        return {
          tone: "negative",
          label: `Reserve not met · Your bid ${amount}`,
        };
      }

      if (data.lot_status === "SOLD") {
        return {
          tone: "positive",
          label: `You won this lot · ${amount}`,
        };
      }

      return {
        tone: "positive",
        label: `The bid is with you · ${amount}`,
      };
    }

    return {
      tone: "negative",
      label: `You have been outbid · Your bid ${amount}`,
    };
  })();

  /*
   * GRID VIEW
   * ------------------------------------------------------------
   * Existing grid behavior is preserved.
   */
  if (viewMode === "grid") {
    return (
      <ArtworkCard
        href="#lot"
        onClick={(event) => {
          event.preventDefault();
          onNavigate();
        }}
        image={image}
        imageAlt={
          [artist, title]
            .filter(Boolean)
            .join(", ") ||
          `Lot ${data.lot_number}`
        }
        eyebrow={
          `Lot ${data.lot_number}` +
          (
            data.auction_name ||
            data.d_name
              ? ` · ${
                  data.auction_name ||
                  data.d_name
                }`
              : ""
          )
        }
        artist={artist}
        title={title}
        compact
        metaSize="1.05rem"
        metaWeight={600}
        favorite={data.fav === 1}
        favoriteOnClick={onFavorite}
        favoriteAriaLabel={
          data.fav === 1
            ? "Remove from favorites"
            : "Add to favorites"
        }
        status={activityStatus}
        meta={activityStatus ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              gap: 0.25,
              maxWidth: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                color: tokens.color.secondary,
                fontWeight: 400,
                ...clamp(1),
              }}
            >
              {priceText}
            </Box>

            {showCurrentBid && (
              <Box
                component="span"
                sx={{
                  ...clamp(1),
                }}
              >
                Current bid: ₱
                {Number(
                  data.hp
                ).toLocaleString()}
              </Box>
            )}

            
          </Box>
        ) : (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              gap: 0.25,
              maxWidth: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                ...clamp(1),
              }}
            >
              {priceText}
            </Box>

            {showCurrentBid && (
              <Box
                component="span"
                sx={{
                  ...clamp(1),
                }}
              >
                Current bid: ₱
                {Number(
                  data.hp
                ).toLocaleString()}
              </Box>
            )}

      
          </Box>
        )}
        action={
          isActive &&
          Number(showHp) !== 1 ? (
            <Button
              variant="text"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onNavigate();
              }}
              sx={{
                justifySelf: "start",
                px: 0,
                minHeight: 34,
                color: tokens.color.ink,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "none",
                borderBottom: `1px solid ${tokens.color.ink}`,
                borderRadius: 0,

                "&:hover": {
                  bgcolor: "transparent",
                  color: tokens.color.muted,
                },
              }}
            >
              Place Bid
            </Button>
          ) : null
        }
      />
    );
  }

  /*
   * LIST VIEW
   * ------------------------------------------------------------
   * Mobile list is intentionally more compact.
   * Desktop list keeps the original sizing.
   *
   * Zebra striping is applied here.
   */
  return (
    <Box
      ref={
        value.__lastBookElementRef ||
        undefined
      }
      sx={{
        height: "100%",
        minWidth: 0,

        display: "grid",

        gridTemplateColumns: {
          xs: "100px 1fr",
          md: "300px 1fr",
        },

        gap: {
          xs: 1.25,
          md: 3,
        },

        color: tokens.color.ink,

        /*
         * ZEBRA STRIPING
         *
         * Every second lot gets a very subtle
         * background so the list is easier to scan.
         */
        "&:nth-of-type(even)": {
          bgcolor: "rgba(0, 0, 0, 0.025)",
        },

        /*
         * Small padding so the alternating background
         * doesn't touch everything too tightly.
         */
        px: {
          xs: 0.75,
          md: 1,
        },

        py: {
          xs: 0.75,
          md: 1,
        },

        boxSizing: "border-box",
      }}
    >
      {/* IMAGE */}
      <Box
        component="a"
        href="#lot"
        aria-label={`View Lot ${data.lot_number}`}
        onClick={(event) => {
          event.preventDefault();
          onNavigate();
        }}
        sx={{
          position: "relative",
          display: "block",
          minWidth: 0,
          cursor: "pointer",
          textDecoration: "none",
          color: "inherit",
          outline: "none",

          "&:hover .lg-frame-img, &:focus-visible .lg-frame-img":
            {
              transform: "scale(1.02)",
            },
        }}
      >
        <ArtworkFrame
          src={image}
          alt={
            [artist, title]
              .filter(Boolean)
              .join(", ") ||
            `Lot ${data.lot_number}`
          }
          ratio="1 / 1"
          mat={tokens.color.ivory}
          padding={{
            xs: 1,
            md: 2,
          }}
        >
          <IconButton
            aria-label={
              data.fav === 1
                ? "Remove from favorites"
                : "Add to favorites"
            }
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onFavorite();
            }}
            sx={{
              position: "absolute",

              top: {
                xs: 6,
                md: 10,
              },

              right: {
                xs: 6,
                md: 10,
              },

              width: {
                xs: 30,
                md: 34,
              },

              height: {
                xs: 30,
                md: 34,
              },

              borderRadius: "50%",

              bgcolor:
                "rgba(255,255,255,0.94)",

              boxShadow:
                "0 1px 3px rgba(0,0,0,0.12)",

              color:
                data.fav === 1
                  ? tokens.color.live
                  : tokens.color.muted,

              zIndex: 2,

              "&:hover": {
                bgcolor: "#fff",
              },
            }}
          >
            {data.fav === 1 ? (
              <FavoriteIcon
                sx={{
                  fontSize: {
                    xs: 16,
                    md: 18,
                  },
                }}
              />
            ) : (
              <FavoriteBorderIcon
                sx={{
                  fontSize: {
                    xs: 16,
                    md: 18,
                  },
                }}
              />
            )}
          </IconButton>
        </ArtworkFrame>
      </Box>

     
     {/* DETAILS */}
<Box
  sx={{
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,

    pt: 0,

    // Keep the whole details area compact
    gap: {
      xs: 0.15,
      md: 0.25,
    },
  }}
>
  {/* LOT NUMBER */}
  <Typography
    variant="overline"
    component="p"
    title={`Lot ${data.lot_number}`}
    sx={{
      ...clamp(1),

      minHeight: "auto",

      fontSize: {
        xs: "0.65rem",
        md: "0.7rem",
      },

      lineHeight: 1.2,

      mb: {
        xs: 0.1,
        md: 0.15,
      },

      wordBreak: "break-word",
    }}
  >
    {`Lot ${data.lot_number}`}
  </Typography>

  {data.isWithdrawn === 1 ? (
    <Typography
      variant="h4"
      component="h3"
      sx={{
        color: tokens.color.live,

        mt: 0,

        ...clamp(2),

        minHeight: "auto",

        fontSize: {
          xs: "0.875rem",
          md: "1.2rem",
        },

        lineHeight: 1.15,

        wordBreak: "break-word",
      }}
    >
      Withdrawn
    </Typography>
  ) : (
    <>
      {/* ARTIST */}
      <Typography
        variant="h4"
        component="h3"
        title={artist || undefined}
        onClick={onNavigate}
        sx={{
          color: tokens.color.ink,

          mt: 0,

          ...clamp(2),

          minHeight: "auto",

          fontSize: {
            xs: "0.9rem",
            md: "1.35rem",
          },

          lineHeight: {
            xs: 1.15,
            md: 1.15,
          },

          wordBreak: "break-word",

          cursor: "pointer",
        }}
      >
        {artist}
      </Typography>

      {/* TITLE */}
      <Typography
        variant="body2"
        component="p"
        title={title || undefined}
        onClick={onNavigate}
        sx={{
          fontStyle: "italic",

          mt: 0.1,

          ...clamp(2),

          minHeight: "auto",

          fontSize: {
            xs: "0.7rem",
            md: "0.82rem",
          },

          lineHeight: {
            xs: 1.2,
            md: 1.3,
          },

          wordBreak: "break-word",

          cursor: "pointer",
        }}
      >
        {title}
      </Typography>

      {/* SIZE */}
      {size && (
        <Typography
          variant="body2"
          component="p"
          title={size}
          sx={{
            mt: 0.15,

            color: tokens.color.secondary,

            ...clamp(1),

            minHeight: "auto",

            fontSize: {
              xs: "0.65rem",
              md: "0.78rem",
            },

            lineHeight: 1.2,

            wordBreak: "break-word",
          }}
        >
          Size: {size}
        </Typography>
      )}
    </>
  )}

  {/* PRICE / BID / ACTIONS */}
  <Box
    sx={{
      mt: {
        xs: 0.5,
        md: 0.75,
      },

      pt: 0,

      display: "grid",

      gap: {
        xs: 0.15,
        md: 0.3,
      },
    }}
  >
    {/* PRICE */}
    <Typography
      variant="body2"
      component="p"
      sx={{
        color: tokens.color.ink,
        fontWeight: 500,

        ...clamp(1),

        minHeight: "auto",

        fontSize: {
          xs: "0.7rem",
          md: "0.84rem",
        },

        lineHeight: 1.2,

        wordBreak: "break-word",
      }}
    >
      {data.isWithdrawn === 1
        ? ""
        : priceText}
    </Typography>

    {/* CURRENT BID */}
    {data.isWithdrawn !== 1 &&
    data.isDone !== 1 &&
    data.hp > 0 ? (
      <Typography
        variant="body2"
        component="p"
        sx={{
          minHeight: "auto",

          fontSize: {
            xs: "0.65rem",
            md: "0.76rem",
          },

          lineHeight: 1.2,

          ...clamp(1),
        }}
      >
        Current bid: ₱
        {Number(data.hp).toLocaleString()}
      </Typography>
    ) : null}

    {/* BID STATUS */}
    {hasBid ? (
      <Typography
        component="p"
        sx={{
          px: {
            xs: 0.6,
            md: 1,
          },

          py: {
            xs: 0.35,
            md: 0.55,
          },

          mt: {
            xs: 0.2,
            md: 0.3,
          },

          fontSize: {
            xs: "0.65rem",
            md: "0.78rem",
          },

          fontWeight: 600,

          lineHeight: 1.25,

          minHeight: "auto",

          display: "flex",
          alignItems: "center",

          color: statusTone.color,
          bgcolor: statusTone.bg,

          borderLeft: `3px solid ${statusTone.color}`,

          wordBreak: "break-word",

          ...clamp(2),
        }}
      >
        {isWinning
          ? data.lot_status === "SOLD"
            ? `Win bid: ₱${highestBid.toLocaleString()}`
            : `Winning: ₱${highestBid.toLocaleString()}`
          : `Outbid: ₱${highestBid.toLocaleString()}`}
      </Typography>
    ) : null}

    {/* PLACE BID */}
    {isActive &&
      Number(showHp) !== 1 && (
        <Button
          variant="text"
          onClick={onNavigate}
          sx={{
            justifySelf: "start",

            px: 0,

            minHeight: {
              xs: 24,
              md: 30,
            },

            mt: {
              xs: 0.1,
              md: 0.2,
            },

            color: tokens.color.ink,

            fontWeight: 600,

            letterSpacing: "0.05em",

            textTransform: "none",

            fontSize: {
              xs: "0.65rem",
              md: "0.82rem",
            },

            borderBottom: `1px solid ${tokens.color.ink}`,

            borderRadius: 0,

            "&:hover": {
              bgcolor: "transparent",
              color: tokens.color.muted,
            },
          }}
        >
          Place Bid
        </Button>
      )}
  </Box>
</Box>
    </Box>
  );
}